import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CalendarDays, Plus, Trash2, Pencil, ChevronDown, MessageSquare } from 'lucide-react';

// Host schedule board (20 Sep 2026, slice 1 - GA board). One row per month:
// which day the meeting is on (or TBC / none) and who is hosting. Not events -
// provisional dates never reach members' calendar feeds. Talks to
// hostBoardRoutes.js on the backend.
//
// Rendered by DashboardStep between the count bar and the Add Event button.
//  - Board off (calendars.host_board_enabled false): nothing is shown. (21 Sep 2026: the
//    on/off switch now lives in Settings > Host Board, with a confirmation, so it is
//    no longer one tap away on the dashboard.)
//  - Board on: the board itself. The active GA gets edit controls; other people
//    with dashboard access see it read-only (canManage comes from the server).
//
// Host names are always the member's Preferred Name (the server guarantees it).
//
// Slice 2 (members): the same component is used on the member page. Members see
// every month in every state, plus the offers made to host it; they can offer to
// host a month that has a meeting and no host yet, and withdraw their own offer.
// Two or more offers on a month = "Contested" (the GA picks the host). A single
// offer shows as the proposed host. The member who has been chosen as host can
// "Confirm I'll host" (once a day is proposed or confirmed) or "Step down as host"
// (before an event exists), which hands the month back.
//
// GA extras (props from DashboardStep / App.js):
//  - "Choose <name>" buttons in a month's editor, one per member who has offered.
//  - "Create event" on a month that has a day (proposed or confirmed): hands a
//    pre-filled event (date, title, host, notes) to onCreateEvent, which opens the
//    normal Add Event form. Saving that form links the month to the event and
//    confirms the day (the server does the linking). (5 Oct 2026, H18 Stage 1) The
//    host no longer needs to be chosen or confirmed first: with no host the event
//    reads "Host wanted", and offers, confirming and stepping down carry on working
//    on the month. The month's host is the event's host (the server keeps them in
//    step); only the day and date of a month with an event are changed via the event.
//  - onBoardChanged (optional): called after the GA changes a month here, so the
//    Notifications bell (whose reconfirmation tasks depend on the board) can refresh.
//  - "Tell members via WhatsApp": onAnnounce opens the usual WhatsApp prompt with
//    a prepared "board updated" message. The count beside it (months changed since
//    members were last told) comes from the server. refreshToken changes whenever
//    events change or an announcement is sent, so the board reloads.

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Row colours. Day state first, then host state. Violet (not orange) for
// "proposed" at Neil's request. Each row also carries a text chip so the state
// never depends on colour alone.
const STATE_STYLES = {
  none: { row: 'bg-gray-100 text-gray-400', chip: 'bg-gray-200 text-gray-500' },
  tbc: { row: 'bg-red-600 text-white', chip: 'bg-red-800 text-white' },
  proposed: { row: 'bg-violet-200 text-violet-900', chip: 'bg-violet-700 text-white' },
  day_confirmed: { row: 'bg-yellow-200 text-yellow-900', chip: 'bg-yellow-700 text-white' },
  complete: { row: 'bg-blue-900 text-white', chip: 'bg-blue-600 text-white' },
};

// (17 Oct 2026, decision 110) The key follows a month's progression: No meeting, then
// Day to be confirmed, Day proposed, Day confirmed host not, Day + host confirmed.
// It covers the row colours only (the brown "Event, host wanted" chip stays out of it).
const LEGEND = [
  ['none', 'No meeting'],
  ['tbc', 'Day to be confirmed'],
  ['proposed', 'Day proposed'],
  // (17 Oct 2026, decision 114) Shortened from "Day confirmed, host not yet" so it fits on one line.
  ['day_confirmed', 'Day confirmed, host not'],
  ['complete', 'Day + host confirmed'],
];

const DAY_STATE_OPTIONS = [
  ['tbc', 'To be confirmed'],
  ['proposed', 'Proposed date'],
  ['confirmed', 'Confirmed date'],
  ['none', 'No meeting (cancelled or skipped)'],
];

const monthLabel = (iso) => `${MONTH_NAMES[Number(iso.slice(5, 7)) - 1]} ${iso.slice(0, 4)}`;

// (6 Oct 2026, H18 Stage 2) A row's name: "May 2027", "May 2027 #2" once the month has
// two meetings, or "May 2027 (one-off)". The server sends it; the fallback is the month.
const slotLabel = (slot) => slot.label || monthLabel(slot.month);

// (24 Oct 2026, decision 132) Phones: the month abbreviated, "Nov 2026", "May 2027 #2",
// "Aug 2027 (one-off)", so the first line of a row never wraps.
const shortSlotLabel = (slot) => slotLabel(slot).replace(
  /^(January|February|March|April|May|June|July|August|September|October|November|December)/,
  (m) => m.slice(0, 3)
);

const dayLabel = (iso) =>
  new Date(Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10))))
    .toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });

// (7 Oct 2026, decision 26) Questions and messages give dates in full ("Friday 27
// November"); the rows keep the compact dayLabel above.
const fullDayLabel = (iso) =>
  new Date(Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10))))
    .toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });

const shortDate = (iso) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

const monthEnd = (iso) =>
  new Date(Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)), 0)).toISOString().slice(0, 10);

// Selectable days for a month's row: every day from today (or the 1st, for a
// future month) to the end of the month, each labelled with its weekday. The GA
// only picks the day - the month and year are already known from the row.
const dayOptionsFor = (monthIso, todayIso) => {
  const lastDay = Number(monthEnd(monthIso).slice(8, 10));
  const minIso = monthIso > todayIso ? monthIso : todayIso;
  const firstDay = minIso <= monthEnd(monthIso) ? Number(minIso.slice(8, 10)) : lastDay + 1;
  const options = [];
  for (let d = firstDay; d <= lastDay; d += 1) {
    const iso = `${monthIso.slice(0, 8)}${String(d).padStart(2, '0')}`;
    const weekday = new Date(Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, d))
      .toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' });
    options.push({ value: iso, label: `${weekday} ${d}` });
  }
  return options;
};

const chipLabel = (slot) => {
  // (6 Oct 2026, H18 Stage 2) One-offs are seen by the administrator only.
  if (slot.isOneOff) return "One-off: members don't see this";
  // (5 Oct 2026, H18 Stage 1) A month with an event still says whether its host is settled.
  if (slot.hasEvent) {
    if (slot.displayState === 'complete') return 'Event created';
    return slot.hasHost ? 'Event, host unconfirmed' : 'Event, host wanted';
  }
  switch (slot.displayState) {
    case 'none': return 'No meeting';
    case 'tbc': return 'Day to be confirmed';
    case 'proposed': return 'Day proposed';
    case 'complete': return 'Day + host confirmed';
    default: return slot.hasHost || slot.hostProposed ? 'Host unconfirmed' : 'Host needed';
  }
};

const dayText = (slot) => {
  if (slot.dayState === 'none') return 'None';
  if (slot.dayState === 'tbc') return 'TBC';
  return slot.date ? dayLabel(slot.date) : 'TBC';
};

// Shown on a month where two or more members have offered to host.
const OFFERS_BADGE = 'Host to be agreed';

// (7 Oct 2026, decision 17) One pair of brackets: "Peter & Samantha (you, offered)",
// not "(you) (offered)". tags = the words that follow the name, in order.
const withTags = (name, tags) => {
  const list = tags.filter(Boolean);
  return list.length ? `${name} (${list.join(', ')})` : name;
};
const youTag = (isMe) => (isMe ? ' (you)' : '');

const hostText = (slot) => {
  if (slot.displayState === 'none') return '';
  if (slot.hasHost) {
    // "Complete" rows already say so in their chip; otherwise show that the host has confirmed.
    // A host who was confirmed by silence is always marked, so it is never mistaken for a reply.
    let confirmed = null;
    if (slot.hostConfirmed) {
      if (slot.confirmedByDefault) confirmed = 'confirmed by default';
      else if (slot.displayState !== 'complete') confirmed = 'confirmed';
    } else {
      // A chosen host who has not confirmed yet: say so, so a bare name is never read as settled.
      confirmed = 'unconfirmed';
    }
    return withTags(slot.hostName || '(name not set)', [slot.iAmHost && 'you', confirmed]);
  }
  if (slot.hostProposed) {
    return withTags(slot.hostProposed.name || '(name not set)', [slot.hostProposed.isMe && 'you', 'offered']);
  }
  if (slot.contested) return 'Several offers';
  return 'No host yet';
};

// One extra line under the host, about the reconfirmation request (slice 7).
const requestNote = (slot) => {
  if (slot.iAmHost && slot.reconfirmRequested) {
    return 'The organiser has asked you to reconfirm: please confirm, or step down.';
  }
  if (slot.reconfirmSentAt && slot.hasHost && !slot.hostConfirmed) {
    return `Reconfirmation requested ${shortDate(slot.reconfirmSentAt)}`;
  }
  return null;
};

const offersText = (slot) =>
  (slot.bidders || []).map((b) => `${b.name || '(name not set)'}${youTag(b.isMe)}`).join(', ');

// (6 Oct 2026, H18 Stage 2) onCancelEventForMonth(eventUid, note): marking a month with an
// event "No meeting" asks whether that meeting is off too; yes cancels the event exactly
// as Delete Event's "The meeting is off" does (WhatsApp and email prompts included).
// (8 Oct 2026, 8.35) onAccessLost (member page): called when the server refuses (403), so
// App.js can check whether this person has been removed instead of showing the refusal here.
const HostBoard = ({ calendar, apiBaseUrl, showStatus, onCreateEvent, onAnnounce, refreshToken, onBoardChanged, onLinkedEventsChange, onCancelEventForMonth, onAccessLost }) => {
  const calendarId = calendar ? calendar.id : null;
  const flagOn = calendar ? calendar.host_board_enabled === true : false;

  const [board, setBoard] = useState(null); // { enabled, slots, canManage }
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  // (24 Oct 2026, decision 132) Phones: the colour key is folded away until opened.
  const [legendOpen, setLegendOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const [addCount, setAddCount] = useState(6);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [members, setMembers] = useState([]);
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [confirmStepDownId, setConfirmStepDownId] = useState(null);
  // (24 Oct 2026, decision 130) Add months, when months are missing from the schedule:
  // { months: ['YYYY-MM', ...], gapLabels: ['March 2027', ...], tailCount } awaiting Continue.
  const [pendingAdd, setPendingAdd] = useState(null);

  const loadSeq = useRef(0);
  // (8 Oct 2026, 8.35) Through a ref, so loadBoard does not change (and reload) on every render.
  const accessLostRef = useRef(onAccessLost);
  accessLostRef.current = onAccessLost;

  const call = useCallback(async (method, path, body) => {
    const res = await fetch(`${apiBaseUrl}/api/calendars/${calendarId}/host-board${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    let data = null;
    try {
      data = await res.json();
    } catch (_) {
      data = null;
    }
    return { ok: res.ok, status: res.status, data };
  }, [apiBaseUrl, calendarId]);

  const loadBoard = useCallback(async () => {
    if (!calendarId) return;
    loadSeq.current += 1;
    const seq = loadSeq.current;
    setLoading(true);
    setLoadError(null);
    try {
      const { ok, status, data } = await call('GET', '');
      if (seq !== loadSeq.current) return;
      if (!ok && status === 403 && accessLostRef.current) accessLostRef.current();
      if (!ok) {
        setBoard(null);
        setLoadError((data && data.error) || 'Could not load the hosting schedule');
      } else {
        setBoard(data);
      }
    } catch (_) {
      if (seq === loadSeq.current) {
        setBoard(null);
        setLoadError('Could not load the hosting schedule');
      }
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [call, calendarId]);

  // (23 Sep 2026) Tell DashboardStep which events are linked to a month on the board
  // (by event_uid). null = no board (off, not loaded, or not the GA's view).
  // (6 Oct 2026, H18 Stage 2) Now an object: { uids: rota events, oneOffUids: one-off
  // events, labels: { event_uid: "May 2027 #2" } } — for the one-off badge and for
  // naming the row in the delete question. ("Not on hosting schedule" has gone: every
  // event on a schedule calendar now has a row.)
  useEffect(() => {
    if (!onLinkedEventsChange) return;
    if (!board || !board.enabled || !board.eventUidBySlot) {
      onLinkedEventsChange(null);
      return;
    }
    const info = { uids: [], oneOffUids: [], labels: {} };
    for (const sl of board.slots || []) {
      const uid = board.eventUidBySlot[sl.id];
      if (!uid) continue;
      if (sl.isOneOff) info.oneOffUids.push(uid);
      else info.uids.push(uid);
      info.labels[uid] = slotLabel(sl);
    }
    onLinkedEventsChange(info);
  }, [board, onLinkedEventsChange]);
  useEffect(() => () => { if (onLinkedEventsChange) onLinkedEventsChange(null); }, [calendarId, onLinkedEventsChange]);

  // Reload when the calendar changes or its on/off flag flips: start clean.
  useEffect(() => {
    setEditingId(null);
    setDraft(null);
    setConfirmRemoveId(null);
    setConfirmStepDownId(null);
    setPendingAdd(null);
    loadBoard();
  }, [loadBoard, flagOn]);

  // (6 Oct 2026, 8.1) refreshToken changes after events change, after an
  // announcement, and now whenever the administrator comes back to the tab
  // (App.js). It used to share the effect above, so every refresh closed an
  // open month editor and lost what had been typed. Now it only reloads the
  // months: the editor (with its unsaved draft) and any "Remove?" or
  // "Step down?" prompt stay open, showing the fresh month behind them.
  // Skipped on first render, where the effect above has already loaded.
  const refreshSeen = useRef(false);
  useEffect(() => {
    if (!refreshSeen.current) {
      refreshSeen.current = true;
      return;
    }
    loadBoard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  if (!calendar) return null;
  if (loading && !board) return null;
  if (loadError) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6 text-sm text-gray-500">
        Hosting schedule: {loadError}
      </div>
    );
  }
  if (!board) return null;

  const canManage = board.canManage === true;
  const slots = board.slots || [];
  const monthCount = new Set(slots.filter((sl) => !sl.isOneOff).map((sl) => sl.month)).size;
  // (7 Oct 2026, decision 1) "8 meetings over 7 months, plus 1 one-off": meetings are rota
  // rows that are not "No meeting"; one-offs are counted separately (administrator only).
  const meetingCount = slots.filter((sl) => !sl.isOneOff && sl.dayState !== 'none').length;
  const oneOffCount = slots.filter((sl) => sl.isOneOff).length;
  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
  const headerCount = `${plural(meetingCount, 'meeting')} over ${plural(monthCount, 'month')}`
    + (oneOffCount > 0 ? `, plus ${plural(oneOffCount, 'one-off')}` : '');
  const todayIso = new Date().toISOString().slice(0, 10);
  // (24 Oct 2026, decision 130) Rota rows per month: a month's only row has no bin (removing
  // it would leave a gap; "No meeting" is the way to skip a month). The server refuses too.
  const rotaRowsInMonth = {};
  for (const sl of slots) {
    if (!sl.isOneOff) rotaRowsInMonth[sl.month] = (rotaRowsInMonth[sl.month] || 0) + 1;
  }
  const canRemove = (slot) => slot.isOneOff || (rotaRowsInMonth[slot.month] || 0) > 1;

  // ---------- board switched off: nothing on the dashboard (the switch is in Settings) ----------
  if (!board.enabled) return null;

  // ---------- editing ----------
  const openEditor = async (slot) => {
    setConfirmRemoveId(null);
    setEditingId(slot.id);
    setDraft({
      dayState: slot.dayState,
      slotDate: slot.date || '',
      hostMemberId: slot.hostMemberId || '',
      hostConfirmed: slot.hostConfirmed,
      note: slot.note || '',
    });
    try {
      const { ok, data } = await call('GET', '/members');
      if (ok && data && Array.isArray(data.members)) setMembers(data.members);
    } catch (_) {
      // The host list is a convenience; the editor still works for the other fields.
    }
  };

  const closeEditor = () => {
    setEditingId(null);
    setDraft(null);
  };

  const saveEditor = async (slot) => {
    const dayHasDate = draft.dayState === 'proposed' || draft.dayState === 'confirmed';
    if (dayHasDate && !draft.slotDate) {
      showStatus('Choose a date for a proposed or confirmed day', 'error', 4000);
      return;
    }

    const body = {};
    if (draft.dayState !== slot.dayState) body.dayState = draft.dayState;
    const newDate = dayHasDate ? draft.slotDate : null;
    if (newDate !== (slot.date || null)) body.slotDate = newDate;

    const newHost = draft.hostMemberId || null;
    const hostChanged = newHost !== (slot.hostMemberId || null);
    if (hostChanged) body.hostMemberId = newHost;
    const wantConfirmed = newHost ? draft.hostConfirmed : false;
    if (hostChanged) {
      // A different host always starts unconfirmed on the server.
      if (wantConfirmed) body.hostConfirmed = true;
    } else if (wantConfirmed !== slot.hostConfirmed) {
      body.hostConfirmed = wantConfirmed;
    }

    const newNote = draft.note.trim();
    if (newNote !== (slot.note || '')) body.note = newNote === '' ? null : newNote;

    if (Object.keys(body).length === 0) {
      closeEditor();
      return;
    }

    setBusy(true);
    try {
      const { ok, data } = await call('PATCH', `/${slot.id}`, body);
      if (!ok) {
        showStatus((data && data.error) || 'Could not save this month', 'error', 6000);
        return;
      }
      setBoard((prev) => ({
        ...prev,
        slots: prev.slots.map((s) => (s.id === slot.id ? data.slot : s)),
      }));
      closeEditor();
      if (onBoardChanged) onBoardChanged();
      if (data.warning) showStatus(data.warning, 'info', 9000);
      else showStatus('Month saved', 'success');
    } catch (_) {
      showStatus('Could not save this month', 'error', 6000);
    } finally {
      setBusy(false);
    }
  };

  const removeSlot = async (slot) => {
    setBusy(true);
    try {
      const { ok, data } = await call('DELETE', `/${slot.id}`);
      if (!ok) {
        showStatus((data && data.error) || 'Could not remove this month', 'error', 6000);
        return;
      }
      setBoard((prev) => ({ ...prev, slots: prev.slots.filter((s) => s.id !== slot.id) }));
      setConfirmRemoveId(null);
      if (onBoardChanged) onBoardChanged();
      showStatus('Month removed', 'success');
      // (7 Oct 2026) The server renumbers the month's other meetings ("#2" becomes plain
      // "February 2027") and the counts change: reload now, not on the next return to the tab.
      await loadBoard();
    } catch (_) {
      showStatus('Could not remove this month', 'error', 6000);
    } finally {
      setBusy(false);
    }
  };

  // (24 Oct 2026, decision 130) Add months puts back every month missing from the schedule,
  // from the first rota month from now on to the last one (as "Day to be confirmed"), then
  // adds the chosen number after the last. When months are missing, the administrator is
  // told first and continues or cancels. (Was, 6 Oct: count forward from the end of the
  // unbroken run, which put back the first missing month, but months already on the
  // schedule used up the number chosen, so fewer were added at the end and a later gap
  // could be missed.)
  const nextMonthOf = (ym) => {
    let y = Number(ym.slice(0, 4));
    let m = Number(ym.slice(5, 7)) + 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
    return `${y}-${String(m).padStart(2, '0')}`;
  };

  const sendAddMonths = async (months) => {
    setBusy(true);
    try {
      const { ok, data } = await call('POST', '/months', { months });
      if (!ok) {
        showStatus((data && data.error) || 'Could not add months', 'error', 6000);
        return;
      }
      let msg = `Added ${data.added} month${data.added === 1 ? '' : 's'}`;
      if (data.skippedTooFar > 0) msg += `; ${data.skippedTooFar} too far ahead and left out`;
      showStatus(msg, 'success', 4000);
      if (onBoardChanged) onBoardChanged();
      await loadBoard();
    } catch (_) {
      showStatus('Could not add months', 'error', 6000);
    } finally {
      setBusy(false);
    }
  };

  const addMonths = () => {
    const currentMonth = todayIso.slice(0, 7);
    const rotaMonths = new Set(
      slots.filter((sl) => !sl.isOneOff).map((sl) => sl.month.slice(0, 7)).filter((ym) => ym >= currentMonth)
    );
    const gaps = [];
    let ym = currentMonth;
    if (rotaMonths.size > 0) {
      const sorted = [...rotaMonths].sort();
      const last = sorted[sorted.length - 1];
      for (ym = sorted[0]; ym <= last; ym = nextMonthOf(ym)) {
        if (!rotaMonths.has(ym)) gaps.push(ym);
      }
      ym = nextMonthOf(last);
    }
    const tail = [];
    for (let i = 0; i < addCount; i += 1) {
      tail.push(ym);
      ym = nextMonthOf(ym);
    }
    const months = [...gaps, ...tail];
    if (gaps.length === 0) {
      sendAddMonths(months);
      return;
    }
    setPendingAdd({ months, gapLabels: gaps.map((g) => monthLabel(`${g}-01`)), tailCount: addCount });
  };

  // "March 2027", "March 2027 and May 2027", "March, May 2027 and June 2027".
  const joinLabels = (list) => (list.length <= 1 ? list.join('') : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`);

  // ---------- members: offer to host / withdraw ----------
  const replaceSlot = (updated) =>
    setBoard((prev) => ({ ...prev, slots: prev.slots.map((s) => (s.id === updated.id ? updated : s)) }));

  const offerToHost = async (slot) => {
    setBusy(true);
    try {
      const { ok, status, data } = await call('POST', `/${slot.id}/bids`);
      if (!ok && status === 403 && accessLostRef.current) { accessLostRef.current(); return; }
      if (!ok) {
        showStatus((data && data.error) || 'Could not record your offer', 'error', 6000);
        await loadBoard(); // the month may have changed under you
        return;
      }
      replaceSlot(data.slot);
      showStatus('Your offer to host has been recorded', 'success', 3000);
    } catch (_) {
      showStatus('Could not record your offer', 'error', 6000);
    } finally {
      setBusy(false);
    }
  };

  const withdrawOffer = async (slot) => {
    setBusy(true);
    try {
      const { ok, status, data } = await call('DELETE', `/${slot.id}/bids/me`);
      if (!ok && status === 403 && accessLostRef.current) { accessLostRef.current(); return; }
      if (!ok) {
        showStatus((data && data.error) || 'Could not withdraw your offer', 'error', 6000);
        await loadBoard();
        return;
      }
      replaceSlot(data.slot);
      showStatus('Your offer has been withdrawn', 'success', 3000);
    } catch (_) {
      showStatus('Could not withdraw your offer', 'error', 6000);
    } finally {
      setBusy(false);
    }
  };

  const chooseHost = async (slot, bidder) => {
    setBusy(true);
    try {
      const { ok, data } = await call('PATCH', `/${slot.id}`, { hostMemberId: bidder.memberId });
      if (!ok) {
        showStatus((data && data.error) || 'Could not choose this host', 'error', 6000);
        return;
      }
      replaceSlot(data.slot);
      closeEditor();
      if (onBoardChanged) onBoardChanged();
      showStatus(`${bidder.name || 'The member'} is now the host for ${slotLabel(slot)}`, 'success', 4000);
    } catch (_) {
      showStatus('Could not choose this host', 'error', 6000);
    } finally {
      setBusy(false);
    }
  };

  const startEvent = (slot) => {
    onCreateEvent({
      slotId: slot.id,
      date: slot.date,
      title: `${calendar.name} \u2013 ${slotLabel(slot)}`,
      // (5 Oct 2026, H18 Stage 1) Blank: the server writes "{host}'s" or "Host wanted".
      location: '',
      hostMemberId: slot.hostMemberId || '',
      notes: slot.note || '',
    });
  };

  const confirmHost = async (slot) => {
    setBusy(true);
    try {
      const { ok, status, data } = await call('POST', `/${slot.id}/confirm-host`);
      if (!ok && status === 403 && accessLostRef.current) { accessLostRef.current(); return; }
      if (!ok) {
        showStatus((data && data.error) || 'Could not confirm', 'error', 6000);
        await loadBoard();
        return;
      }
      replaceSlot(data.slot);
      showStatus(`Thank you - you are confirmed as host for ${slotLabel(slot)}`, 'success', 4000);
    } catch (_) {
      showStatus('Could not confirm', 'error', 6000);
    } finally {
      setBusy(false);
    }
  };

  const stepDown = async (slot) => {
    setBusy(true);
    try {
      const { ok, status, data } = await call('POST', `/${slot.id}/step-down`);
      if (!ok && status === 403 && accessLostRef.current) { accessLostRef.current(); return; }
      if (!ok) {
        showStatus((data && data.error) || 'Could not step down', 'error', 6000);
        await loadBoard();
        return;
      }
      replaceSlot(data.slot);
      showStatus(`You have stepped down as host for ${slotLabel(slot)}`, 'success', 4000);
    } catch (_) {
      showStatus('Could not step down', 'error', 6000);
    } finally {
      setConfirmStepDownId(null);
      setBusy(false);
    }
  };

  const renderEditor = (slot) => {
    // (5 Oct 2026, H18 Stage 1) With an event, only the day and date are locked here.
    const locked = Boolean(slot.eventId);
    const dayHasDate = draft.dayState === 'proposed' || draft.dayState === 'confirmed';
    const dayOptions = dayOptionsFor(slot.month, todayIso);
    const knownHost = members.some((m) => m.id === draft.hostMemberId);
    // (6 Oct 2026, H18 Stage 2) With an event, the Day list offers only its current
    // state and "No meeting"; choosing "No meeting" asks whether that meeting is off too
    // (the event and the month must never tell different stories).
    const eventUid = board.eventUidBySlot ? board.eventUidBySlot[slot.id] : null;
    const canCallOff = locked && !slot.isOneOff && Boolean(eventUid) && typeof onCancelEventForMonth === 'function';
    const askMeetingOff = canCallOff && draft.dayState === 'none';
    const dayStateOptions = locked
      ? DAY_STATE_OPTIONS.filter(([value]) => value === slot.dayState || (canCallOff && value === 'none'))
      : DAY_STATE_OPTIONS;
    return (
      <div className="bg-white text-gray-800 rounded-lg border border-gray-200 p-3 mt-2 space-y-3">
        {locked && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
            This month already has an event, so its day is changed through the event. The host can still be
            changed here; the event follows.{canCallOff ? ' If the meeting is off, choose "No meeting" under Day.' : ''}
          </p>
        )}
        {!slot.hasHost && slot.bidders && slot.bidders.length > 0 && (
          <div className="rounded-lg border border-violet-200 bg-violet-50 p-2.5">
            <p className="text-xs font-semibold text-violet-900 mb-1.5">Members who have offered to host</p>
            <div className="flex flex-wrap gap-2">
              {slot.bidders.map((b) => (
                <button
                  key={b.memberId}
                  type="button"
                  onClick={() => chooseHost(slot, b)}
                  disabled={busy}
                  className="px-2.5 py-1 text-xs font-medium rounded bg-white text-violet-900 border border-violet-300 hover:bg-violet-100 disabled:opacity-50"
                >
                  {`Choose ${b.name || '(name not set)'}`}
                </button>
              ))}
            </div>
            <p className="text-xs text-violet-800 mt-1.5">Pressing the 'Choose...' button will save immediately and clear any other offers.</p>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block text-xs font-semibold text-gray-600">
            Day
            <select
              value={draft.dayState}
              disabled={locked && !canCallOff}
              onChange={(e) => setDraft({ ...draft, dayState: e.target.value })}
              className="mt-1 block w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg font-normal disabled:bg-gray-100"
            >
              {dayStateOptions.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>
          {dayHasDate && (
            <label className="block text-xs font-semibold text-gray-600">
              Day of {MONTH_NAMES[Number(slot.month.slice(5, 7)) - 1]}
              <select
                value={draft.slotDate}
                disabled={locked}
                onChange={(e) => setDraft({ ...draft, slotDate: e.target.value })}
                className="mt-1 block w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg font-normal disabled:bg-gray-100"
              >
                <option value="">Choose day</option>
                {draft.slotDate && !dayOptions.some((o) => o.value === draft.slotDate) && (
                  <option value={draft.slotDate}>{dayLabel(draft.slotDate)}</option>
                )}
                {dayOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
          )}
          <label className="block text-xs font-semibold text-gray-600">
            Host
            <select
              value={draft.hostMemberId}
              onChange={(e) => setDraft({ ...draft, hostMemberId: e.target.value, hostConfirmed: false })}
              className="mt-1 block w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg font-normal disabled:bg-gray-100"
            >
              <option value="">(no host)</option>
              {draft.hostMemberId && !knownHost && (
                <option value={draft.hostMemberId}>{slot.hostName || '(current host)'}</option>
              )}
              {members.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 sm:self-end sm:pb-1.5">
            <input
              type="checkbox"
              checked={Boolean(draft.hostMemberId) && draft.hostConfirmed}
              disabled={!draft.hostMemberId}
              onChange={(e) => setDraft({ ...draft, hostConfirmed: e.target.checked })}
            />
            Host confirmed
          </label>
        </div>
        <label className="block text-xs font-semibold text-gray-600">
          Notes
          <input
            type="text"
            value={draft.note}
            maxLength={500}
            onChange={(e) => setDraft({ ...draft, note: e.target.value })}
            placeholder="e.g. Cancelled - Christmas break"
            className="mt-1 block w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg font-normal"
          />
        </label>
        {askMeetingOff ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-2.5 space-y-2">
            <p className="text-sm font-semibold text-red-900">
              {`There's an event on ${slot.date ? fullDayLabel(slot.date) : 'this month'}. Is that meeting off too?`}
            </p>
            <p className="text-xs text-red-800">
              Yes deletes the event and shows this month as "No meeting", with the note above
              {draft.note.trim() ? '' : ' (or "Cancelled")'}. You can then tell members, as when deleting an event.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  onCancelEventForMonth(eventUid, draft.note.trim() || 'Cancelled');
                  closeEditor();
                }}
                disabled={busy}
                className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                Yes, the meeting is off
              </button>
              <button
                type="button"
                onClick={() => setDraft({ ...draft, dayState: slot.dayState })}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                No, keep it
              </button>
            </div>
          </div>
        ) : (
        <div className="flex gap-2">
          <button
            onClick={() => saveEditor(slot)}
            disabled={busy}
            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            Save
          </button>
          <button
            onClick={closeEditor}
            disabled={busy}
            className="px-3 py-1.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
        )}
      </div>
    );
  };

  // (24 Oct 2026, decision 132) The parts of a month's row, shared by the phone layout
  // (two lines) and the wider one (one line), so both always show the same things.
  const renderHostBlock = (slot) => (
    <>
                      <span>{hostText(slot)}</span>
                      {slot.contested && slot.displayState !== 'none' && (
                        <span className="block text-xs opacity-90">{offersText(slot)}</span>
                      )}
                      {requestNote(slot) && <span className="block text-xs font-medium opacity-90">{requestNote(slot)}</span>}
                      {board.viewer === 'member' && slot.canStepDown && confirmStepDownId === slot.id && (
                        <span className="block text-xs font-semibold">Step down as host for {slotLabel(slot)}?</span>
                      )}
                      {slot.note && <span className="block text-xs opacity-80">{slot.note}</span>}
                      {/* (23 Sep 2026) Removing a month never deletes its event, so say so
                          before the GA confirms - otherwise the event quietly stays on the
                          calendar with nothing on the board to show for it. */}
                      {canManage && confirmRemoveId === slot.id && slot.eventId && (
                        <span className="block text-xs font-semibold">
                          This month has an event. Removing the month leaves the event on the calendar; if the
                          meeting is off, delete the event from the event list as well.
                        </span>
                      )}
    </>
  );
  const renderActions = (slot) => (
    <>
                      {slot.contested && slot.displayState !== 'none' && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap bg-white text-gray-900 border border-gray-300">
                          {OFFERS_BADGE}
                        </span>
                      )}
                      {board.viewer === 'member' && slot.canBid && (
                        <button
                          onClick={() => (slot.iBid ? withdrawOffer(slot) : offerToHost(slot))}
                          disabled={busy}
                          className="px-2.5 py-1 text-xs font-medium rounded bg-white text-gray-800 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
                        >
                          {slot.iBid ? 'Withdraw my offer' : 'Offer to host'}
                        </button>
                      )}
                      {board.viewer === 'member' && slot.canConfirmHost && (
                        <button
                          onClick={() => confirmHost(slot)}
                          disabled={busy}
                          className="px-2.5 py-1 text-xs font-medium rounded bg-white text-blue-800 border border-blue-300 hover:bg-blue-50 disabled:opacity-50 whitespace-nowrap"
                        >
                          Confirm I'll host
                        </button>
                      )}
                      {board.viewer === 'member' && slot.canStepDown && (
                        confirmStepDownId === slot.id ? (
                          <>
                            <button
                              onClick={() => stepDown(slot)}
                              disabled={busy}
                              className="px-2.5 py-1 text-xs font-medium rounded bg-white text-red-700 border border-red-300 hover:bg-red-50 disabled:opacity-50 whitespace-nowrap"
                            >
                              Yes, step down
                            </button>
                            <button
                              onClick={() => setConfirmStepDownId(null)}
                              className="px-2.5 py-1 text-xs font-medium rounded bg-white text-gray-700 border border-gray-300 whitespace-nowrap"
                            >
                              No, keep hosting
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => setConfirmStepDownId(slot.id)}
                            disabled={busy}
                            className="px-2.5 py-1 text-xs font-medium rounded bg-white text-gray-800 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
                          >
                            Step down as host
                          </button>
                        )
                      )}
                      {canManage && !slot.hasEvent && (slot.dayState === 'proposed' || slot.dayState === 'confirmed') && slot.date && typeof onCreateEvent === 'function' && (
                        <button
                          onClick={() => startEvent(slot)}
                          className="px-2 sm:px-2.5 py-1 text-xs font-medium rounded bg-white text-blue-800 border border-blue-300 hover:bg-blue-50 whitespace-nowrap"
                        >
                          Create event
                        </button>
                      )}
    </>
  );
  const renderChip = (slot, style) => (
                      <span className={`px-1.5 sm:px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${style.chip}`}>
                        {chipLabel(slot)}
                      </span>
  );
  const renderAdminIcons = (slot) => (
    <>
                      {canManage && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => (editingId === slot.id ? closeEditor() : openEditor(slot))}
                            aria-label={`Edit ${slotLabel(slot)}`}
                            className="p-1.5 rounded bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          {/* (24 Oct 2026, decision 130) No bin on a month's only row. When asked,
                              "Keep?" comes first, where the bin was, so a double tap on the bin
                              lands on "Keep?" rather than removing the row. */}
                          {!canRemove(slot) ? null : confirmRemoveId === slot.id ? (
                            <>
                              <button
                                onClick={() => setConfirmRemoveId(null)}
                                className="px-2 py-1 text-xs rounded bg-white text-gray-600 border border-gray-300"
                              >
                                {/* (24 Oct 2026, decision 130) "Keep?" (was "No"), Neil's wording. */}
                                Keep?
                              </button>
                              <button
                                onClick={() => removeSlot(slot)}
                                disabled={busy}
                                className="px-2 py-1 text-xs rounded bg-white text-red-700 border border-red-300 hover:bg-red-50"
                              >
                                {slot.eventId ? 'Remove month only?' : 'Remove?'}
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => setConfirmRemoveId(slot.id)}
                              aria-label={`Remove ${slotLabel(slot)}`}
                              className="p-1.5 rounded bg-white text-red-600 border border-red-200 hover:bg-red-50"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      )}
    </>
  );

  // ---------- the board ----------
  return (
    <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
      <div className="flex items-center justify-between gap-2">
        {/* (21 Sep 2026) The heading never breaks mid-title; on a narrow phone the
            "N months · updated ..." line drops underneath it instead. */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 min-w-0">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-blue-600" />
            <h2 className="font-semibold text-gray-800 whitespace-nowrap">Hosting schedule</h2>
          </div>
          <span className="text-xs text-gray-500 whitespace-nowrap">
            {/* (6 Oct 2026, H18 Stage 2) Months, not rows: a second meeting or a one-off
                does not add a month. (7 Oct 2026, decision 1) Meetings and one-offs shown too. */}
            {headerCount}
            {board.lastChangedAt ? ` \u00b7 updated ${shortDate(board.lastChangedAt)}` : ''}
          </span>
        </div>
        <button
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? 'Show hosting schedule' : 'Hide hosting schedule'}
          className="p-1 text-gray-500 hover:text-gray-800"
        >
          <ChevronDown className={`w-5 h-5 transition-transform ${collapsed ? '' : 'rotate-180'}`} />
        </button>
      </div>

      {!collapsed && (
        <div className="mt-3">
          {/* (17 Oct 2026, decision 110) Wide screens: the five chips in one row, spread
              evenly across the card. Phones: a two-column grid of equal-width chips. A long
              label may wrap inside its own chip rather than push the others out. */}
          {/* (24 Oct 2026, decision 132, amending 110 and 114 for phones) On a phone the key
              is folded away behind "Colour key", which opens it; wide screens always show it. */}
          <button
            type="button"
            onClick={() => setLegendOpen((o) => !o)}
            aria-expanded={legendOpen}
            className="sm:hidden flex items-center gap-1 text-xs font-medium text-gray-600 mb-2"
          >
            Colour key
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${legendOpen ? 'rotate-180' : '-rotate-90'}`} />
          </button>
          <div className={`${legendOpen ? 'grid' : 'hidden'} sm:grid grid-cols-2 sm:grid-cols-5 gap-1.5 mb-3`}>
            {LEGEND.map(([key, label]) => (
              <span
                key={key}
                className={`flex items-center justify-center text-center leading-snug px-1.5 sm:px-2 py-0.5 rounded text-xs ${STATE_STYLES[key].row}`}
              >
                {label}
              </span>
            ))}
          </div>

          {board.viewer === 'member' && slots.some((sl) => sl.canBid) && (
            <p className="text-sm text-gray-600 mb-3">
              Would you like to host a month? Tap "Offer to host" on it. If more than one member offers, you will be
              asked to agree between yourselves.
            </p>
          )}

          {slots.length === 0 && (
            <p className="text-sm text-gray-500 py-2">
              {canManage
                ? 'No months on the board yet. Add the months you want to plan.'
                : 'The hosting schedule has not been set up yet.'}
            </p>
          )}

          <div className="space-y-2">
            {slots.map((slot) => {
              const style = STATE_STYLES[slot.displayState] || STATE_STYLES.tbc;
              return (
                <div key={slot.id}>
                  {/* (24 Oct 2026, decision 132) Two layouts, chosen by screen width: phones get
                      the two-line row below; wider screens keep the one-line row (month + day |
                      host | chips and buttons), whose chips and buttons drop to a right-aligned
                      second line in a narrow container such as the member page. */}
                  {/* (24 Oct 2026, decision 132) Phones: two lines, a smaller font. Line 1: the
                      month (abbreviated) and day, with the chip on the right. Line 2: the host
                      (and any notes) on the left, the buttons on the right. */}
                  <div className={`sm:hidden px-2.5 py-2 rounded-lg text-sm ${style.row}`}>
                    <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                      <span className="whitespace-nowrap">
                        <span className="font-semibold">{shortSlotLabel(slot)}</span>
                        {` \u00b7 ${dayText(slot)}`}
                      </span>
                      <span className="ml-auto">{renderChip(slot, style)}</span>
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 mt-1">
                      {/* min-w: a wide pair of buttons (e.g. "Confirm I'll host" / "Step down as
                          host") drops below the name instead of squeezing it into a narrow column. */}
                      <div className="min-w-[8rem] flex-1">{renderHostBlock(slot)}</div>
                      <div className="flex flex-wrap items-center justify-end gap-1 ml-auto">
                        {renderActions(slot)}
                        {renderAdminIcons(slot)}
                      </div>
                    </div>
                  </div>
                  {/* Wider screens: as before, one line (the chips and buttons drop to a
                      right-aligned second line in a narrow container). */}
                  <div className={`hidden sm:flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2 rounded-lg ${style.row}`}>
                    <div className="flex items-baseline gap-3 shrink-0">
                      {/* (7 Oct 2026) min-w + nowrap: "October 2026 (one-off)" no longer wraps. */}
                      <span className="min-w-[9rem] shrink-0 whitespace-nowrap font-semibold">{slotLabel(slot)}</span>
                      <span className="w-24 shrink-0">{dayText(slot)}</span>
                    </div>
                    <div className="flex-1 min-w-[12rem]">{renderHostBlock(slot)}</div>
                    <div className="flex flex-wrap items-center justify-end gap-1.5 ml-auto">
                      {/* Order, left to right: offers badge, member buttons, then the day
                          chip - so the chip sits at the right-hand end of every row (just
                          before the GA's edit/remove icons) whatever else the row shows. */}
                      {renderActions(slot)}
                      {renderChip(slot, style)}
                      {renderAdminIcons(slot)}
                    </div>
                  </div>
                  {canManage && editingId === slot.id && draft && renderEditor(slot)}
                </div>
              );
            })}
          </div>

          {/* (21 Sep 2026) Nothing to announce until the board has at least one month. */}
          {canManage && slots.length > 0 && typeof onAnnounce === 'function' && (
            <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-3 border-t border-gray-100">
              <p className="text-sm text-gray-600">
                {typeof board.unannouncedCount !== 'number'
                  ? 'Let members know when you have updated the board.'
                  : board.unannouncedCount > 0
                    ? (board.unannouncedCount === 1
                      ? '1 meeting has changed since members were last informed.'
                      : `${board.unannouncedCount} meetings have changed since members were last informed.`)
                    : 'Members are up to date with the latest changes'}
              </p>
              <button
                onClick={onAnnounce}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
              >
                <MessageSquare className="w-4 h-4" />
                Tell members via WhatsApp
              </button>
            </div>
          )}

          {canManage && (
            <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-3 border-t border-gray-100">
              {/* (7 Oct 2026, decision 10) The number first, then the button that acts on it. */}
              <div className="flex items-center gap-2">
                <select
                  value={addCount}
                  onChange={(e) => setAddCount(Number(e.target.value))}
                  aria-label="How many months to add"
                  className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                >
                  {[3, 6, 9, 12].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
                <button
                  onClick={addMonths}
                  disabled={busy || Boolean(pendingAdd)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-50 disabled:opacity-50"
                >
                  <Plus className="w-4 h-4" />
                  Add months
                </button>
              </div>
              {/* (24 Oct 2026, decision 130) Told before months missing from the schedule are put back. */}
              {pendingAdd && (
                <div className="w-full rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-2">
                  <p className="text-sm text-blue-900">
                    {`${joinLabels(pendingAdd.gapLabels)} ${pendingAdd.gapLabels.length === 1 ? 'is' : 'are'} missing from the hosting schedule and will be put back as 'Day to be confirmed'. `}
                    {`${pendingAdd.tailCount} month${pendingAdd.tailCount === 1 ? '' : 's'} will also be added at the end.`}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => { const months = pendingAdd.months; setPendingAdd(null); sendAddMonths(months); }}
                      disabled={busy}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      Continue
                    </button>
                    <button
                      type="button"
                      onClick={() => setPendingAdd(null)}
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HostBoard;
